import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Check, ChevronDown, UserCog } from 'lucide-react';
import {
  CRM_PROJECT_STATUS, CRM_STAGE, CRM_STAGE_ORDER, CRM_STAGE_OWNER, PRIORITY, dateLabel, dateTimeLabel,
  type CrmProjectStatus, type CrmStage, type Priority,
} from '@adisys/shared';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import {
  MetaBadge, Notice, StageBadge, errorText, inr, projectKey, useCrmMeta, useReloadProject,
} from '../../lib/crm';
import { PageHeader } from '../../components/AppShell';
import {
  Button, Card, CardHeader, EmptyState, ErrorState, Field, Modal, Select, Skeleton, StatusBadge, Tabs, Textarea, cx, useToast,
} from '../../components/ui';
import { OverviewTab } from './project/OverviewTab';
import { SurveyTab } from './project/SurveyTab';
import { BoqTab } from './project/BoqTab';
import { PricingTab } from './project/PricingTab';
import { QuotationsTab } from './project/QuotationsTab';
import { ProcurementTab } from './project/ProcurementTab';
import { FinanceTab } from './project/FinanceTab';
import { DocumentsTab } from './project/DocumentsTab';

const TABS = [
  { key: 'overview', label: 'Overview' }, { key: 'survey', label: 'Site Survey' }, { key: 'boq', label: 'BOQ' },
  { key: 'pricing', label: 'Pricing' }, { key: 'quotations', label: 'Quotations' },
  { key: 'procurement', label: 'Procurement' }, { key: 'finance', label: 'Finance' }, { key: 'documents', label: 'Documents' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/* ------------------------------------------------------------------ */
function Stepper({ stage, status }: { stage: CrmStage; status: string }) {
  const current = CRM_STAGE_ORDER.indexOf(stage);
  const done = stage === 'completed';
  return (
    <ol className="flex gap-1 overflow-x-auto pb-1" aria-label="Project stage">
      {CRM_STAGE_ORDER.map((s, i) => {
        const state = done || i < current ? 'done' : i === current ? 'current' : 'upcoming';
        return (
          <li key={s} aria-current={state === 'current' ? 'step' : undefined}
            className={cx('min-w-[104px] flex-1 rounded-lg px-2.5 py-2 ring-1 ring-inset',
              state === 'done' && 'bg-success-soft ring-success/20',
              state === 'current' && (status === 'active' ? 'bg-brand-50 ring-brand-500' : 'bg-warning-soft ring-warning/30'),
              state === 'upcoming' && 'bg-card ring-line')}>
            <div className="flex items-center gap-1.5">
              <span className={cx('flex h-4.5 w-4.5 h-[18px] w-[18px] items-center justify-center rounded-full text-[12px] font-bold',
                state === 'done' ? 'bg-success text-white' : state === 'current' ? 'bg-brand-500 text-white' : 'bg-ink-100 text-ink-500')}>
                {state === 'done' ? <Check className="h-3 w-3" aria-hidden /> : i + 1}
              </span>
              <span className={cx('text-xs font-medium', state === 'upcoming' ? 'text-ink-500' : 'text-ink-900')}>
                {CRM_STAGE.byValue[s].label}
              </span>
            </div>
            <p className="mt-0.5 pl-6 text-[12px] text-ink-500">
              {state === 'current' && !done ? `${CRM_STAGE_OWNER[s]} team` : CRM_STAGE_OWNER[s]}
            </p>
          </li>
        );
      })}
    </ol>
  );
}

/* ------------------------------------------------------------------ */
function Timeline({ projectId }: { projectId: string }) {
  const q = useQuery({
    queryKey: ['crm', 'project', projectId, 'activity'],
    queryFn: () => api.get(`/crm/projects/${projectId}/activity`),
  });
  const rows: any[] = q.data?.data ?? [];
  const groups = new Map<string, any[]>();
  for (const r of rows) {
    const day = new Date(r.createdAt).toDateString();
    groups.set(day, [...(groups.get(day) ?? []), r]);
  }
  const dayLabel = (d: string) => {
    const diff = Math.round((new Date(new Date().toDateString()).getTime() - new Date(d).getTime()) / 86_400_000);
    return diff === 0 ? 'Today' : diff === 1 ? 'Yesterday' : dateLabel(new Date(d).toISOString().slice(0, 10));
  };

  return (
    <Card>
      <CardHeader title="Activity" subtitle="Every step on this project, newest first" />
      {q.isLoading ? <div className="space-y-3"><Skeleton className="h-10" /><Skeleton className="h-10" /><Skeleton className="h-10" /></div>
        : q.isError ? <ErrorState error={q.error} onRetry={() => q.refetch()} />
        : rows.length === 0 ? <EmptyState title="No activity yet" />
        : (
          <div className="max-h-[640px] space-y-4 overflow-y-auto pr-1">
            {[...groups.entries()].map(([day, items]) => (
              <div key={day}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-400">{dayLabel(day)}</p>
                <ul className="space-y-3 border-l border-line pl-4">
                  {items.map((a) => (
                    <li key={a.id} className="relative">
                      <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-ink-300 ring-2 ring-card" aria-hidden />
                      <p className="text-sm text-ink-800">{a.summary}</p>
                      <p className="mt-0.5 text-xs text-ink-500">{a.actorName ?? 'System'} · {dateTimeLabel(a.createdAt)}</p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
type ModalKind = null | 'status' | 'stage' | 'assign';

export function CrmProjectDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const reload = useReloadProject(id);
  const meta = useCrmMeta();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.key === params.get('tab'))?.key ?? 'overview') as TabKey;

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [modal, setModal] = useState<ModalKind>(null);
  const [nextStatus, setNextStatus] = useState<'on_hold' | 'active' | 'lost' | 'cancelled'>('on_hold');
  const [reason, setReason] = useState('');
  const [stage, setStage] = useState<CrmStage>('boq');
  const [sales, setSales] = useState('');
  const [tech, setTech] = useState('');
  const [fieldErr, setFieldErr] = useState('');

  useEffect(() => {
    const close = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const q = useQuery({ queryKey: projectKey(id), queryFn: () => api.get(`/crm/projects/${id}`) });
  const project = q.data;

  const done = (msg: string) => () => { toast.success(msg); setModal(null); setReason(''); setFieldErr(''); reload(); void qc.invalidateQueries({ queryKey: ['crm', 'project', id, 'activity'] }); };
  const fail = (e: unknown) => setFieldErr(errorText(e));

  const statusM = useMutation({
    mutationFn: () => api.post(`/crm/projects/${id}/status`, { status: nextStatus, reason }),
    onSuccess: done('Project status updated'), onError: fail,
  });
  const stageM = useMutation({
    mutationFn: () => api.post(`/crm/projects/${id}/stage`, { stage, reason }),
    onSuccess: done('Stage changed'), onError: fail,
  });
  const claimM = useMutation({
    mutationFn: () => api.post(`/crm/projects/${id}/claim`),
    onSuccess: done('You now own the technical work'), onError: (e) => toast.error('Could not take ownership', errorText(e)),
  });
  const assignM = useMutation({
    mutationFn: () => api.patch(`/crm/projects/${id}`, {
      ...(sales && sales !== project?.salesExecutiveId ? { salesExecutiveId: sales } : {}),
      ...(tech && tech !== project?.technicalOwnerId ? { technicalOwnerId: tech } : {}),
    }),
    onSuccess: done('Assignment updated'), onError: fail,
  });

  if (q.isLoading) {
    return (<><PageHeader title="Project" /><div className="space-y-4 p-4 sm:p-6"><Skeleton className="h-24" /><Skeleton className="h-16" /><Skeleton className="h-64" /></div></>);
  }
  if (q.isError || !project) {
    return (<><PageHeader title="Project" /><div className="p-4 sm:p-6"><Card><ErrorState error={q.error} onRetry={() => q.refetch()} /></Card></div></>);
  }

  const canStatus = can('crm.lead.update', 'crm.stage.override');
  const canAssignSales = can('crm.lead.view.all', 'crm.stage.override');
  const canAssignTech = can('crm.boq.approve', 'crm.lead.update', 'crm.stage.override');
  const canClaim = can('crm.boq.manage') && !project.technicalOwnerId && project.status === 'active';
  const inactive = project.status !== 'active' && project.status !== 'completed';
  const statusMeta = CRM_PROJECT_STATUS.byValue[project.status as CrmProjectStatus];
  const pr = PRIORITY.byValue[project.priority as Priority];

  const openStatus = (s: typeof nextStatus) => { setNextStatus(s); setReason(''); setFieldErr(''); setModal('status'); setMenuOpen(false); };
  const menuItems: Array<{ label: string; onClick: () => void; danger?: boolean }> = [];
  if (canStatus) {
    if (project.status === 'active') menuItems.push({ label: 'Put on hold', onClick: () => openStatus('on_hold') });
    if (project.status === 'on_hold') menuItems.push({ label: 'Resume project', onClick: () => openStatus('active') });
    if (project.status === 'active' || project.status === 'on_hold') {
      menuItems.push({ label: 'Mark as lost', onClick: () => openStatus('lost'), danger: true });
      menuItems.push({ label: 'Cancel project', onClick: () => openStatus('cancelled'), danger: true });
    }
    if ((project.status === 'lost' || project.status === 'cancelled') && can('crm.stage.override')) {
      menuItems.push({ label: 'Reopen project', onClick: () => openStatus('active') });
    }
  }
  if (canClaim) menuItems.push({ label: 'Take technical ownership', onClick: () => { setMenuOpen(false); claimM.mutate(); } });
  if (canAssignSales || canAssignTech) {
    menuItems.push({ label: 'Assign people…', onClick: () => { setSales(project.salesExecutiveId ?? ''); setTech(project.technicalOwnerId ?? ''); setFieldErr(''); setModal('assign'); setMenuOpen(false); } });
  }
  if (can('crm.stage.override')) {
    menuItems.push({ label: 'Move to stage…', onClick: () => { setStage(project.stage); setReason(''); setFieldErr(''); setModal('stage'); setMenuOpen(false); } });
  }

  const people = meta.data?.people;
  const verb = { on_hold: 'Put project on hold', active: 'Resume project', lost: 'Mark project as lost', cancelled: 'Cancel project' }[nextStatus];

  return (
    <>
      <PageHeader
        title={project.name}
        description={`${project.projectCode} · ${project.customerName}`}
        actions={
          <>
            <Link to="/crm/projects"><Button icon={<ArrowLeft className="h-4 w-4" />}>Pipeline</Button></Link>
            {menuItems.length > 0 && (
              <div ref={menuRef} className="relative">
                <Button onClick={() => setMenuOpen((o) => !o)} icon={<UserCog className="h-4 w-4" />} aria-haspopup="menu" aria-expanded={menuOpen}>
                  Actions<ChevronDown className="h-3.5 w-3.5" aria-hidden />
                </Button>
                {menuOpen && (
                  <div role="menu" className="absolute right-0 z-30 mt-1 w-60 rounded-lg bg-card py-1 shadow-overlay ring-1 ring-line">
                    {menuItems.map((m) => (
                      <button key={m.label} role="menuitem" onClick={m.onClick}
                        className={cx('block w-full px-3.5 py-2 text-left text-sm hover:bg-ink-50', m.danger ? 'text-danger' : 'text-ink-800')}>
                        {m.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        }>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
          <StageBadge stage={project.stage} />
          <MetaBadge meta={CRM_PROJECT_STATUS} value={project.status} />
          <StatusBadge tone={pr.tone} dot={false}>{pr.label} priority</StatusBadge>
          <span className="text-ink-500">Value <strong className="tabular text-ink-900">{inr(project.value, true)}</strong></span>
          <span className="text-ink-500">Sales <strong className="text-ink-900">{project.salesExecutiveName ?? 'Unassigned'}</strong></span>
          <span className="text-ink-500">Technical <strong className="text-ink-900">{project.technicalOwnerName ?? 'Unassigned'}</strong></span>
          <span className="text-ink-500">Expected close <strong className="text-ink-900">{dateLabel(project.expectedCloseDate)}</strong></span>
        </div>
      </PageHeader>

      <div className="space-y-4 p-4 sm:p-6">
        {inactive && (
          <Notice tone="warning">
            <span className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                This project is <strong>{statusMeta.label.toLowerCase()}</strong>, so no stage can advance and no documents can change.
                {project.statusReason ? ` Reason: ${project.statusReason}` : ''}
                {project.status === 'on_hold' && canStatus ? ' Resume it from the Actions menu to continue.' : ''}
              </span>
            </span>
          </Notice>
        )}

        <Card>
          <Stepper stage={project.stage} status={project.status} />
          {project.status === 'active' && project.stage !== 'completed' && (
            <p className="mt-2 text-xs text-ink-500">
              Waiting on <strong className="text-ink-800">{project.nextOwner}</strong> to complete{' '}
              <strong className="text-ink-800">{CRM_STAGE.byValue[project.stage as CrmStage].label}</strong>.
            </p>
          )}
        </Card>

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
          <div className="min-w-0 space-y-4">
            <Tabs tabs={TABS.map((t) => ({ key: t.key, label: t.label }))} active={tab}
              onChange={(k) => { const n = new URLSearchParams(params); n.set('tab', k); setParams(n, { replace: true }); }} />
            <Card>
              {tab === 'overview' && <OverviewTab project={project} reload={reload} />}
              {tab === 'survey' && <SurveyTab project={project} reload={reload} />}
              {tab === 'boq' && <BoqTab project={project} reload={reload} />}
              {tab === 'pricing' && <PricingTab project={project} reload={reload} />}
              {tab === 'quotations' && <QuotationsTab project={project} reload={reload} />}
              {tab === 'procurement' && <ProcurementTab project={project} reload={reload} />}
              {tab === 'finance' && <FinanceTab project={project} reload={reload} />}
              {tab === 'documents' && <DocumentsTab project={project} reload={reload} />}
            </Card>
          </div>
          <Timeline projectId={id} />
        </div>
      </div>

      {/* ---- status ---- */}
      <Modal open={modal === 'status'} onClose={() => setModal(null)} title={verb} size="sm"
        description="The reason is recorded on the project timeline and the audit log."
        footer={<>
          <Button onClick={() => setModal(null)}>Back</Button>
          <Button variant={nextStatus === 'active' ? 'primary' : 'danger'} loading={statusM.isPending}
            disabled={reason.trim().length < 3} onClick={() => statusM.mutate()}>Confirm</Button>
        </>}>
        {(nextStatus === 'lost' || nextStatus === 'cancelled') && (
          <div className="mb-3"><Notice tone="warning">Open quotations that have not been accepted will be withdrawn.</Notice></div>
        )}
        <Field label="Reason" required error={fieldErr}>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />
        </Field>
      </Modal>

      {/* ---- stage override ---- */}
      <Modal open={modal === 'stage'} onClose={() => setModal(null)} title="Move to stage" size="sm"
        footer={<>
          <Button onClick={() => setModal(null)}>Back</Button>
          <Button variant="danger" loading={stageM.isPending} disabled={reason.trim().length < 5 || stage === project.stage}
            onClick={() => stageM.mutate()}>Override stage</Button>
        </>}>
        <div className="space-y-3">
          <Notice tone="danger">
            This bypasses the workflow. Nothing is created or checked for the skipped stages — stock, pricing and
            approvals stay as they are. Use it only to correct a project that is in the wrong place.
          </Notice>
          <Field label="New stage" required>
            <Select value={stage} onChange={(e) => setStage(e.target.value as CrmStage)}>
              {CRM_STAGE.list.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Select>
          </Field>
          <Field label="Reason" required error={fieldErr} hint="At least 5 characters. Shown on the timeline.">
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />
          </Field>
        </div>
      </Modal>

      {/* ---- assignment ---- */}
      <Modal open={modal === 'assign'} onClose={() => setModal(null)} title="Assign people" size="sm"
        footer={<>
          <Button onClick={() => setModal(null)}>Back</Button>
          <Button variant="primary" loading={assignM.isPending} onClick={() => assignM.mutate()}>Save</Button>
        </>}>
        <div className="space-y-3">
          {canAssignSales && (
            <Field label="Sales executive">
              <Select value={sales} onChange={(e) => setSales(e.target.value)}>
                <option value="">Choose…</option>
                {(people?.sales ?? []).map((u: any) => <option key={u.id} value={u.id}>{u.fullName}</option>)}
              </Select>
            </Field>
          )}
          {canAssignTech && (
            <Field label="Technical owner">
              <Select value={tech} onChange={(e) => setTech(e.target.value)}>
                <option value="">Choose…</option>
                {(people?.technical ?? []).map((u: any) => <option key={u.id} value={u.id}>{u.fullName}</option>)}
              </Select>
            </Field>
          )}
          {fieldErr && <p className="text-xs text-danger">{fieldErr}</p>}
        </div>
      </Modal>
    </>
  );
}
